import type { RouteRecordRaw } from 'vue-router'
import { guardCreation360, guardCreationEvaluation, guardCreationPeriod, guardCreationProject, guardEditEvaluation, guardEvaluationAccess, guardCreationProjectSimplified, guardSummaries, guardSummaryCreation, guardSettingsAccess, guardEvaluationFormsSettingsAccess, guardEvaluationAveragesSettingsAccess } from '@/config/router/navigationsGuards/guardEvaluation'

const MODULE_NAME = 'EVALUATION'
const MODULE_ID = 7

const evaluationRoutes: Array<RouteRecordRaw> = [
  {
    path: '/evaluation/external/:evaluationId',
    meta: {
      requiresAuth: false,
      isExternal: true,
    },
    name: 'EvaluationExternal',
    component: () => import('@/views/evaluation/Evaluation.vue'),
  },
  {
    path: '/evaluation',
    meta: {
      requiresAuth: true,
    },
    beforeEnter: [guardEvaluationAccess],
    children: [
      {
        path: 'campaign',
        children: [
          {
            path: '',
            name: 'EvaluationCampaigns',
            component: () => import('@/views/evaluation/campaign/EvaluationCampaigns.vue'),
            meta: {
              requiresAuth: true,
            },
          },
          {
            path: ':campaignId',
            name: 'EvaluationCampaign',
            component: () => import('@/views/evaluation/campaign/EvaluationCampaign.vue'),
            meta: {
              requiresAuth: true,
            },
          },
        ],
      },
      {
        path: 'form/:formId/edit',
        name: 'EvaluationFormEdit',
        component: () => import('@/views/evaluation/form/Edit.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluationFormEdit',
          editRouteName: 'EvaluationFormEdit',
        },
        beforeEnter: [guardEvaluationFormsSettingsAccess],
      },
      {
        path: 'form/new',
        name: 'EvaluationFormNew',
        component: () => import('@/views/evaluation/form/Edit.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluationFormNew',
          editRouteName: 'EvaluationFormEdit',
        },
        beforeEnter: [guardEvaluationFormsSettingsAccess],
      },
      {
        path: '',
        name: 'Evaluations',
        component: () => import('@/views/evaluation/List.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluations',
        },
        beforeEnter: [],
      },
      {
        path: 'stats',
        name: 'EvaluationsStats',
        component: () => import('@/views/evaluation/Stats.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluationsStats',
        },
        beforeEnter: [],
      },
      {
        path: ':evaluationId',
        name: 'Evaluation',
        component: () => import('@/views/evaluation/Evaluation.vue'),
        meta: {
          requiresAuth: true,
        },
        beforeEnter: [],
      },
      {
        path: ':evaluationId/edit',
        name: 'EvaluationEdit',
        component: () => import('@/views/evaluation/EvaluationEdit.vue'),
        meta: {
          requiresAuth: true,
        },
        beforeEnter: [guardEditEvaluation],
      },
      {
        path: 'create',
        name: 'EvaluationCreation',
        component: () => import('@/views/evaluation/creation/EvaluationCreation.vue'),
        redirect(to) {
          return { name: 'EvaluationCreationSelection' }
        },
        children: [
          {
            path: '',
            name: 'EvaluationCreationSelection',
            component: () => import('@/views/evaluation/creation/Selection.vue'),
            meta: {
              requiresAuth: true,
              titleTranslationKey: 'pageTitle.evaluationCreationSelection',
            },
            beforeEnter: [guardCreationEvaluation],
          },
          {
            path: '360',
            name: 'EvaluationCreation360',
            component: () => import('@/views/evaluation/creation/360.vue'),
            meta: {
              requiresAuth: true,
              titleTranslationKey: 'pageTitle.evaluationCreation360',
            },
            beforeEnter: [guardCreation360],
          },
          {
            path: 'period',
            name: 'EvaluationCreationPeriod',
            component: () => import('@/views/evaluation/creation/Period.vue'),
            meta: {
              requiresAuth: true,
              titleTranslationKey: 'pageTitle.evaluationCreationPeriod',
            },
            beforeEnter: [guardCreationPeriod],
          },
          {
            path: 'project',
            name: 'EvaluationCreationProject',
            component: () => import('@/views/evaluation/creation/Project.vue'),
            meta: {
              requiresAuth: true,
              titleTranslationKey: 'pageTitle.evaluationCreationProject',
            },
            beforeEnter: [guardCreationProject],
          },
          {
            path: 'simplified',
            name: 'EvaluationCreationSimplified',
            component: () => import('@/views/evaluation/custom/Simplified.vue'),
            meta: {
              requiresAuth: true,
            },
            beforeEnter: [guardCreationProjectSimplified],
          }
        ],
      },
      {
        path: 'summaries',
        children:[
          {
            path: '',
            name: 'EvaluationSummaries',
            component: () => import('@/views/evaluation/summary/List.vue'),
            meta: {
              requiresAuth: true,
            },
            beforeEnter: [guardSummaries],
          },
          {
            path: 'create',
            name: 'EvaluationSummaryCreation',
            component: () => import('@/views/evaluation/summary/Create.vue'),
            meta: {
              requiresAuth: true,
            },
            beforeEnter: [guardSummaryCreation],
          },
          {
            path: ':summaryId',
            name: 'EvaluationSummaryDetail',
            component: () => import('@/views/evaluation/summary/Detail.vue'),
            meta: {
              requiresAuth: true,
            },
          }
        ]
      },
      {
        path: 'settings',
        name: 'EvaluationSettings',
        component: () => import('@/views/evaluation/Settings.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluationSettings',
        },
        beforeEnter: [guardSettingsAccess],
      },
      {
        path: 'settings/averages/new',
        name: 'EvaluationAverageNew',
        component: () => import('@/views/evaluation/average/Edit.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluationAverageNew',
        },
        beforeEnter: [guardEvaluationAveragesSettingsAccess],
      },
      {
        path: 'settings/averages/:averageId',
        name: 'EvaluationAverageEdit',
        component: () => import('@/views/evaluation/average/Edit.vue'),
        meta: {
          requiresAuth: true,
          titleTranslationKey: 'pageTitle.evaluationAverageEdit',
        },
        beforeEnter: [guardEvaluationAveragesSettingsAccess],
      },

    ],

  },

]

export default evaluationRoutes.map(route => ({ ...route, meta: { ...route.meta, moduleName: MODULE_NAME, moduleId: MODULE_ID } }))
